import {
  DragDropContext,
  Draggable,
  type DropResult,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import cx from "classnames";
import { isEmpty } from "lodash";
import { useParams } from "next/navigation";
import {
  type ChangeEventHandler,
  type ReactElement,
  type SetStateAction,
  useContext,
} from "react";
import {
  MdDragHandle,
  MdOutlineCheckBox,
  MdOutlineCheckBoxOutlineBlank,
} from "react-icons/md";
import shallow from "zustand/shallow";

import Link from "@/components/MyLink";
import SetListTitleBar from "@/components/SetListTitleBar";
import SkeletonSongList from "@/components/SkeletonSongList";
import { ServerContext } from "@/context/Server";
import { useApp, useAppStore } from "@/store/app";
import {
  useReplicacheFriends,
  useReplicacheSetLists,
  useReplicacheSongIdMap,
  useReplicacheSongs,
} from "@/store/replicache";
import formatSecondsAsMinutesSeconds from "@/utils/formatSecondsAsMinutesSeconds";
import fromNowOrNow from "@/utils/fromNowOrNow";
import { getSongListRoute, getSongViewRoute } from "@/utils/getSongRoute";
import searchAndSortSongs from "@/utils/searchAndSortSongs";
import type { SetListId, SongId, SortKey, User } from "@/utils/types";

const LOG_TAG = "🔤 SongList";
const DEBUG = false;

export default function SongList() {
  const { session } = useContext(ServerContext);
  const { setListModify, songViewCountIncrement } = useApp();
  const { songs } = useReplicacheSongs();
  const { songIdMap } = useReplicacheSongIdMap();
  const { friends } = useReplicacheFriends();
  const hasFriends = !isEmpty(friends);
  const { setLists } = useReplicacheSetLists();

  const {
    isSearchOpen,
    includeFriendsSongs,
    setIncludeFriendsSongs,
    sortKey: _sortKey,
    setSortKey,
    searchKey,
    user,
  } = useAppStore(
    ({
      isSearchOpen,
      includeFriendsSongs,
      setIncludeFriendsSongs,
      sortKey,
      setSortKey,
      searchKey,
      user,
    }) => ({
      isSearchOpen,
      includeFriendsSongs,
      setIncludeFriendsSongs,
      sortKey,
      setSortKey,
      searchKey,
      user,
    }),
    shallow,
  );

  const { setListId, songId } = useParams() as {
    setListId: SetListId;
    songId: SongId;
  };

  const setList = setListId && !isSearchOpen ? setLists[setListId] : null;
  const limitToSongIds = setList?.songIds;
  /*
  console.debug(LOG_TAG, "setList init", {
    setList,
    setListId,
    songIds: limitToSongIds?.map(songId => songs[songId]?.title),
  });
  */
  const defaultSortKey = session ? "recent" : "title";
  const sortKey = _sortKey === null ? defaultSortKey : _sortKey;

  // The only case where songIdMap should be empty is when we're loading from repliacache
  // either from indexedDB or from a sync after a login.
  const showSkeleton = isEmpty(songIdMap);

  const showSortAndFriendsFilter = !isSearchOpen && !setList;
  const showFirstLetter = !isSearchOpen && sortKey !== "recent" && !setList;
  const showSetListAugmentations: boolean = !!(!isSearchOpen && setList);

  const userId = user?.id;
  const { songsListNoDuplicates, totalDuration } = searchAndSortSongs({
    includeFriendsSongs: includeFriendsSongs,
    limitToSongIds,
    searchKey: searchKey,
    songs,
    songIdMap,
    sortKey,
    userId,
  });
  // console.debug(LOG_TAG, "render", {
  //   totalDuration,
  //   songsListNoDuplicatesLength: songsListNoDuplicates.length,
  //   songIdMapLength: Object.keys(songIdMap).length,
  // });
  const handleSort = (e: { target: { value: SetStateAction<string> } }) => {
    if (DEBUG) console.debug(LOG_TAG, "handleSort sortKey", e.target.value);
    setSortKey(e.target.value as SortKey);
  };

  const handleSortDrop = (result: DropResult) => {
    if (DEBUG) console.debug(LOG_TAG, "handleSortDrop", { result });
    if (!setList) throw new Error("Sort without setList");

    // dropped outside the list
    if (!result.destination) return;

    const removedIndex = result.source.index;
    const addedIndex = result.destination.index;

    if (removedIndex === addedIndex) return;
    // This is an opportunity to clean out any deleted songIds from the set list
    const newSortSongIds = [...setList.songIds].filter(
      (songId) => songIdMap[songId].deleted_timestamp === 0,
    );
    if (DEBUG)
      console.debug(LOG_TAG, "handleSortDrop filtered", { newSortSongIds });
    newSortSongIds.splice(
      addedIndex,
      0,
      newSortSongIds.splice(removedIndex, 1)[0],
    );
    if (DEBUG)
      console.debug(LOG_TAG, "handleSortDrop", {
        removedIndex,
        addedIndex,
        setListSongIds: setList.songIds.map((songId) => songs[songId]?.title),
        newSortSongIds: newSortSongIds.map((songId) => songs[songId]?.title),
      });
    const newSetList = {
      ...setList,
      songIds: newSortSongIds,
    };
    setListModify(newSetList);
  };

  /*
  console.debug(LOG_TAG, {
    showSkeleton,
    //limitToSongIds,
    songsListNoDuplicatesLength: Object.keys(songsListNoDuplicates).length,
    songsLength: Object.keys(songs).length,
    songIdMapLength: Object.keys(songIdMap).length,
  });
  */

  if (searchKey && songsListNoDuplicates.length === 0) {
    return (
      <div className="p-4 text-accent-content text-base">
        {`No results in your Songbook for "${searchKey}"`}
      </div>
    );
  }

  if (showSkeleton) {
    /*
    console.debug("SongList skeletor", {
      songsLoading,
      user,
      isSyncedInitial,
    });
    */
    return (
      <div className="px-2">
        <SkeletonSongList />
      </div>
    );
  } /*else {
    console.debug("SongList NON-skeletor", {
      songsLoading,
      user,
      isSyncedInitial,
    });
  }*/
  const primaryTextIsTitle = sortKey === "title" || sortKey === "recent";

  // if (songsListNoDuplicates.length && songsListNoDuplicates.length > 13) {
  //   console.debug(
  //     LOG_TAG,
  //     "RENDER",
  //     songsListNoDuplicates.map(song => song.title),
  //   );
  // }

  return (
    <>
      {showSortAndFriendsFilter && user && (
        <SongListSortAndFilter
          {...{
            sortKey: sortKey,
            handleSort,
            user,
            hasFriends,
            includeFriendsSongs: includeFriendsSongs,
            setIncludeFriendsSongs: setIncludeFriendsSongs,
          }}
        />
      )}
      {showSetListAugmentations && setList && (
        <SetListTitleBar setList={setList} totalDuration={totalDuration} />
      )}
      <div>
        <SortableContainer {...{ handleSortDrop, showSetListAugmentations }}>
          {songsListNoDuplicates.map((song, index) => {
            const primaryText = primaryTextIsTitle ? song.title : song.artist;
            const secondaryText = primaryTextIsTitle ? song.artist : song.title;

            let firstLetter = primaryText.charAt(0).toUpperCase();

            if (index !== 0) {
              const priorSong = songsListNoDuplicates[index - 1];
              if (!priorSong) {
                //debugger;
                throw new Error("No prior song");
              }
              const priorSongPrimaryText = primaryTextIsTitle
                ? priorSong.title
                : priorSong.artist;
              const priorSongFirstLetter = priorSongPrimaryText
                .charAt(0)
                .toUpperCase();
              firstLetter =
                priorSongFirstLetter !== firstLetter ? firstLetter : "";
            }
            // Not sure how we get undefined here for `songIdMap[song.id]` but it's happened.
            const title = songIdMap[song.id]
              ? `Last viewed: ${fromNowOrNow(
                  songIdMap[song.id].last_viewed_timestamp,
                )}`
              : "";
            const rootClickPath = getSongListRoute({ setList });
            const songViewRoute = getSongViewRoute({ song, setList });
            const clickSongHref =
              song.id === songId ? rootClickPath : songViewRoute;
            return (
              <SortableDraggable
                key={song.id}
                draggableId={song.id}
                {...{ showSetListAugmentations, index }}
              >
                <Link
                  href={clickSongHref}
                  title={title}
                  onClick={() => songViewCountIncrement(song.id)}
                  className={cx(
                    "flex items-center border-l-4 py-2 pr-4 pl-3 text-accent-content cursor-pointer",
                    songId === song.id
                      ? "border-primary"
                      : "border-transparent hover:border-base-content",
                  )}
                >
                  {showFirstLetter && (
                    <div className="self-start pt-1 min-w-[36px] font-medium text-xl">
                      {firstLetter}
                    </div>
                  )}
                  <div className="py-1.5 flex-1">
                    <div className="text-base text-accent-content">
                      {primaryText}
                      {!!song.duration && setListId && (
                        <span className="text-sm text-secondary-content">
                          {" - "}
                          {formatSecondsAsMinutesSeconds(song.duration)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-secondary-content">
                      {secondaryText}
                    </div>
                  </div>
                  {showSetListAugmentations && (
                    <div className="drag-handle min-w-[24px] cursor-move ml-auto">
                      <MdDragHandle className="text-2xl" />
                    </div>
                  )}
                </Link>
              </SortableDraggable>
            );
          })}
        </SortableContainer>
      </div>
    </>
  );
}

const SortableContainer = ({
  children,
  handleSortDrop,
  showSetListAugmentations,
}: {
  children: ReactElement | ReactElement[];
  handleSortDrop: OnDragEndResponder;
  showSetListAugmentations: boolean;
}): ReactElement => {
  if (!showSetListAugmentations) return <>{children}</>;
  return (
    <DragDropContext onDragEnd={handleSortDrop}>
      <Droppable droppableId="droppable">
        {(droppableProvided, _droppableSnapshot) => (
          <div ref={droppableProvided.innerRef}>
            {children}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const SortableDraggable = ({
  children,
  draggableId,
  showSetListAugmentations,
  index,
}: {
  children: ReactElement;
  draggableId: SongId;
  showSetListAugmentations: boolean;
  index: number;
}): ReactElement => {
  if (!showSetListAugmentations) return children;
  return (
    <Draggable {...{ draggableId, index }}>
      {(provided, snapshot) => (
        <div
          className={snapshot.isDragging ? "isDragging" : undefined}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          {children}
        </div>
      )}
    </Draggable>
  );
};

const SongListSortAndFilter = ({
  sortKey,
  handleSort,
  user,
  hasFriends,
  includeFriendsSongs,
  setIncludeFriendsSongs,
}: {
  sortKey: SortKey;
  handleSort: ChangeEventHandler<HTMLSelectElement>;
  user: User;
  hasFriends: boolean;
  includeFriendsSongs: boolean;
  setIncludeFriendsSongs: (T: boolean) => void;
}) => {
  return (
    <div className="grid grid-cols-3 px-2.5 py-0">
      <div className="col-span-2 flex items-center">
        <div className="flex justify-between items-center m-2 rounded relative cursor-pointer w-[120px] text-accent-content">
          <select
            value={sortKey}
            onChange={handleSort}
            className="select w-full"
          >
            {user && (
              <option
                value="recent"
                className="px-4 py-2 hover:bg-[#ffffff10] cursor-pointer"
              >
                Recent
              </option>
            )}
            <option
              value="title"
              className="px-4 py-2 hover:bg-[#ffffff10] cursor-pointer"
            >
              Title
            </option>
            <option
              value="artist"
              className="px-4 py-2 hover:bg-[#ffffff10] cursor-pointer"
            >
              Artist
            </option>
          </select>
        </div>
      </div>
      {hasFriends && (
        <div
          className="text-right flex items-center"
          onClick={() => setIncludeFriendsSongs(!includeFriendsSongs)}
        >
          <div className="btn btn-circle text-2xl btn-ghost text-accent-content">
            {includeFriendsSongs ? (
              <MdOutlineCheckBox />
            ) : (
              <MdOutlineCheckBoxOutlineBlank />
            )}
          </div>
          <div className="text-base">Friends</div>
        </div>
      )}
    </div>
  );
};
